"use client"

import {
  createContext,
  use,
  type ComponentProps,
  type ReactNode,
} from "react"
import { ChevronLeft } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"


type OnboardingBackAction = {
  disabled: boolean
  onClick: () => void
}

const OnboardingStepActionsContext =
  createContext<OnboardingBackAction | null>(null)

export function OnboardingStepActionsProvider({
  backAction,
  children,
}: {
  backAction: OnboardingBackAction | null
  children: ReactNode
}) {
  return (
    <OnboardingStepActionsContext value={backAction}>
      {children}
    </OnboardingStepActionsContext>
  )
}

/**
 * Shared action row for every onboarding step. The layout supplies its
 * registry-derived Back action, which appears immediately before step actions.
 */
export function OnboardingStepActions({
  backAction: backActionOverride,
  children,
  className,
  ...props
}: ComponentProps<"div"> & {
  backAction?: OnboardingBackAction | null
}) {
  const tUi = useTranslations("Onboarding.OnboardingOnboardingStepActions")

  const contextualBackAction = use(OnboardingStepActionsContext)
  const backAction =
    backActionOverride === undefined
      ? contextualBackAction
      : backActionOverride

  return (
    <div
      className={cn(
        "flex flex-col items-stretch gap-3 border-t pt-5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end",
        className,
      )}
      {...props}
    >
      {backAction ? (
        <Button
          type="button"
          variant="outline"
          disabled={backAction.disabled}
          onClick={backAction.onClick}
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          <span key={tUi("back")}>{tUi("back")}</span></Button>
      ) : null}
      {children}
    </div>
  )
}
